/**
=========================================================
* Trust Cargo - notifications (the container workflow)
=========================================================

* What is waiting to be done, across every container.
*
* Each line is one step of one container, and it appears on its own once its
* date has come - so many days after the container loaded, or after it was
* inspected, or after it reached Hebron. Nothing is scheduled: the server
* works out what is due each time this is opened, which is what lets a
* corrected date move the whole workflow with it.
*
* Two answers, and only two:
*   تم    - done. It never comes back, and it keeps who pressed it.
*   تخطي  - skipped. It comes back in two days.
*
* Some steps carry a date of their own - the port inspection, the arrival in
* Hebron - and those ask for it before they accept تم, because the steps
* after them count from that date.
*/

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";

import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import Pagination, { usePagination } from "components/Pagination";
import { recordLink } from "utils/openRecord";
import { statusLabel } from "utils/containerStatus";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/** Who is pressing the button. Recorded, not trusted - see the controller. */
const currentUser = () => ({
  user_id: Number(sessionStorage.getItem("user_id")) || null,
  user_name: sessionStorage.getItem("user_name") || sessionStorage.getItem("role_id") || "unknown",
});

const today = () => new Date().toISOString().slice(0, 10);

/**
 * How late a step is, said the way somebody would say it out loud. The
 * number alone ("7") reads as a quantity of something rather than an age.
 */
function lateness(days) {
  if (days <= 0) return { text: "today", tone: "info" };
  if (days === 1) return { text: "1 day late", tone: "warning" };
  if (days <= 7) return { text: `${days} days late`, tone: "warning" };
  return { text: `${days} days late`, tone: "error" };
}

/**
 * How the office would say which shipment this is: the file number they use
 * out loud, the number painted on the box, and who is carrying it. Empty
 * parts are dropped rather than shown as dashes - a dash is not an answer,
 * and three of them are noise.
 */
const shipmentOf = (row) =>
  [row.container_name, row.container_no, row.shipping_line]
    .map((v) => String(v || "").trim())
    .filter((v) => v && v !== "0" && v !== "-")
    .join("  ·  ");

/** What the measurement notice is still missing, in the sheet's own words. */
const NEEDS_LABEL = {
  total_cbm: "ناقص T.CBM",
  total_kg: "ناقص T.KG",
  delivery_date: "ناقص تاريخ التسليم",
};

/**
 * The order or container a notice is about - and the way to it.
 *
 * A notice names its record, and the name is what gets clicked to open it:
 * the order's or the container's own form, with everything on it and
 * editable. The row as a whole cannot be the link - it holds the answer
 * buttons and the date boxes, and a click meant for one of those must not
 * take the page away. So the name is a button, and says so with its icon.
 */
function RecordLink({ row, children }) {
  const navigate = useNavigate();

  return (
    <SuiBox
      component="button"
      type="button"
      onClick={() => navigate(recordLink(row))}
      title={
        // eslint-disable-next-line no-nested-ternary
        row.order_id
          ? "Open this order"
          : row.opens === "view_container"
          ? "Open this container's prices"
          : "Open this container"
      }
      sx={({ palette, borders, functions: { rgba } }) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        maxWidth: "100%",
        px: 1,
        py: 0.35,
        mx: -1,
        border: "none",
        borderRadius: borders.borderRadius.md,
        backgroundColor: "transparent",
        color: palette.dark.main,
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: 700,
        textAlign: "start",
        cursor: "pointer",
        transition: "background-color 140ms ease",
        "& .open-icon": { fontSize: "16px !important", color: palette.info.main },
        "&:hover, &:focus-visible": {
          backgroundColor: rgba(palette.info.main, 0.1),
          textDecoration: "underline",
        },
      })}
    >
      {children}
      <Icon className="open-icon">open_in_new</Icon>
    </SuiBox>
  );
}

RecordLink.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  row: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

/** One thing waiting to be done. */
function Task({ row, onDone, onSkip, busy }) {
  const navigate = useNavigate();
  // the steps that write a date cannot be finished without one
  const [date, setDate] = useState(today);
  const [asking, setAsking] = useState(false);

  const late = lateness(row.overdue_days);
  const needsDate = Boolean(row.fills);

  const confirm = () => {
    if (needsDate && !asking) {
      setAsking(true);
      return;
    }
    onDone(row, needsDate ? date : null);
  };

  return (
    <SuiBox
      sx={({ palette, borders, functions: { rgba } }) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        px: 2.5,
        py: 2,
        borderRadius: borders.borderRadius.lg,
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        borderInlineStartWidth: 4,
        borderInlineStartColor: palette[late.tone].main,
        backgroundColor: rgba(palette[late.tone].main, 0.04),
      })}
    >
      <SuiBox sx={{ flex: "1 1 340px", minWidth: 0 }}>
        {/* what to do */}
        <SuiTypography sx={{ fontSize: 16.5, fontWeight: 800, lineHeight: 1.3 }}>
          {row.label}
        </SuiTypography>

        {/* which shipment - named, not coded */}
        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.6 }}>
          <Icon sx={{ fontSize: "17px !important", opacity: 0.5 }}>inventory_2</Icon>
          <RecordLink row={row}>{shipmentOf(row)}</RecordLink>

          {row.container_status && (
            <Chip
              // the same word the containers screen uses - `loading` is LOADED
              label={statusLabel(row.container_status)}
              size="small"
              sx={({ palette, functions: { rgba } }) => ({
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "capitalize",
                backgroundColor: rgba(palette.info.main, 0.14),
                color: palette.info.main,
              })}
            />
          )}
        </SuiBox>

        {/**
         * Why it is being asked for today. Without this the only honest
         * reading of "136 days late" is "something is wrong and I do not
         * know what" - the line names the date it was counted from, so the
         * answer to a wrong-looking notice is to fix that date.
         */}
        {row.because && (
          <SuiBox sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
            <Icon sx={{ fontSize: "15px !important", opacity: 0.45 }}>schedule</Icon>
            <SuiTypography sx={{ fontSize: 12.5, opacity: 0.72 }}>
              {`${row.because}  ←  استحق ${row.due_on}`}
            </SuiTypography>
          </SuiBox>
        )}

        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.8 }}>
          <Chip
            label={late.text}
            size="small"
            sx={({ palette, functions: { rgba } }) => ({
              height: 22,
              fontWeight: 700,
              fontSize: 11.5,
              backgroundColor: rgba(palette[late.tone].main, 0.16),
              color: palette[late.tone].main,
            })}
          />

          {/**
           * The B/L notice is three requests, each a switch on the container.
           * It names the ones still off, so a notice that stays after the
           * B/L was asked for says why it is still here. They are switched
           * from the container's own form - its name above opens it - and تم
           * switches on whatever is left.
           */}
          {(row.needs || []).map((n) => (
            <Chip
              key={n.name}
              label={`ناقص: ${n.label}`}
              size="small"
              sx={({ palette, functions: { rgba } }) => ({
                height: 22,
                fontSize: 11.5,
                fontWeight: 700,
                backgroundColor: rgba(palette.warning.main, 0.16),
                color: palette.warning.focus,
              })}
            />
          ))}

          {/* it has been put off before - worth saying, so a step that keeps
              being pushed back is visible as such */}
          {row.state === "skipped" && (
            <Chip
              label={`أُجّل بواسطة ${row.user_name || "أحدهم"}`}
              size="small"
              sx={{ height: 22, fontSize: 11.5, opacity: 0.75 }}
            />
          )}
        </SuiBox>
      </SuiBox>

      {asking && (
        <TextField
          label={row.fills === "port_inspection_date" ? "Inspection date" : "Arrival date"}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 190 }}
        />
      )}

      <SuiBox sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        {/**
         * A notice that goes on its own has no تم - the CBM prices one is
         * finished by the prices, and a button would let it be silenced with
         * the boxes still empty. What it offers instead is the way to the
         * screen they are typed on; the other answer, تخطي, is unchanged.
         */}
        {row.auto ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={busy}
            onClick={() => navigate(recordLink(row))}
            startIcon={<Icon>edit_note</Icon>}
            sx={{ minWidth: 96 }}
          >
            تعبئة الأسعار
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={busy}
            onClick={confirm}
            startIcon={<Icon>check</Icon>}
            sx={{ minWidth: 96 }}
          >
            {asking ? "Save" : "تم"}
          </Button>
        )}

        <Button
          variant="outlined"
          color="dark"
          size="small"
          disabled={busy}
          onClick={() => (asking ? setAsking(false) : onSkip(row))}
          sx={{ minWidth: 96 }}
        >
          {asking ? "Cancel" : "تخطي"}
        </Button>
      </SuiBox>
    </SuiBox>
  );
}

/**
 * One thing waiting on an order, rather than on a container.
 *
 * Neither chase notice is ever "marked done" - each disappears because the
 * thing it asked for happened. So the buttons are what a person can actually
 * answer:
 *
 *   check          تخطي, which brings it back tomorrow, or cancel the order.
 *   measure        the three measurements, typed here; filling them makes the
 *                  order READY and the notice goes on its own.
 *   delivery_note  تم - the note was read and dealt with - or move the
 *                  container's loading day, which pushes the reminder with it
 *                  rather than dismissing it.
 */
function OrderTask({ row, onAnswer, busy }) {
  const [asking, setAsking] = useState("");
  const [cbm, setCbm] = useState(Number(row.total_cbm) > 0 ? String(row.total_cbm) : "");
  const [kg, setKg] = useState(Number(row.total_kg) > 0 ? String(row.total_kg) : "");
  const [delivery, setDelivery] = useState(row.delivery_date || today());
  const [loadingDay, setLoadingDay] = useState(row.expected_loading_date || today());

  const late = lateness(row.overdue_days);
  const isNote = row.step === "delivery_note";
  // the missing-data notice asks for the same three figures, so it is
  // answered the same way: typed in here, all three together
  const isMeasure = row.step === "measure" || row.step === "missing";

  const subject = [row.order_code, row.shop_code, row.customer]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join("  ·  ");

  return (
    <SuiBox
      sx={({ palette, borders, functions: { rgba } }) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        px: 2.5,
        py: 2,
        borderRadius: borders.borderRadius.lg,
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        borderInlineStartWidth: 4,
        borderInlineStartColor: palette[late.tone].main,
        backgroundColor: rgba(palette[late.tone].main, 0.04),
      })}
    >
      <SuiBox sx={{ flex: "1 1 340px", minWidth: 0 }}>
        <SuiTypography sx={{ fontSize: 16.5, fontWeight: 800, lineHeight: 1.3 }}>
          {row.label}
        </SuiTypography>

        {/* which order, the way it is called out loud */}
        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.6 }}>
          <Icon sx={{ fontSize: "17px !important", opacity: 0.5 }}>receipt_long</Icon>
          <RecordLink row={row}>{subject}</RecordLink>

          {row.goods && (
            <Chip
              label={row.goods}
              size="small"
              sx={({ palette, functions: { rgba } }) => ({
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: rgba(palette.info.main, 0.14),
                color: palette.info.main,
              })}
            />
          )}
        </SuiBox>

        {/* the note is the whole point of its reminder - shown, not summarised */}
        {isNote && row.delivery_notes && (
          <SuiTypography sx={{ fontSize: 13.5, fontWeight: 600, mt: 0.6 }}>
            {`«${row.delivery_notes}»`}
          </SuiTypography>
        )}

        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
          <Icon sx={{ fontSize: "15px !important", opacity: 0.45 }}>schedule</Icon>
          <SuiTypography sx={{ fontSize: 12.5, opacity: 0.72 }}>
            {isNote
              ? `يوم التحميل المتوقع ${row.due_on}`
              : `الطلبية بتاريخ ${row.order_date}  ←  استحق ${row.due_on}`}
          </SuiTypography>
        </SuiBox>

        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.8 }}>
          <Chip
            label={late.text}
            size="small"
            sx={({ palette, functions: { rgba } }) => ({
              height: 22,
              fontWeight: 700,
              fontSize: 11.5,
              backgroundColor: rgba(palette[late.tone].main, 0.16),
              color: palette[late.tone].main,
            })}
          />

          {/* what the measurement notice is still waiting for, by name */}
          {isMeasure &&
            (row.needs || []).map((n) => (
              <Chip
                key={n}
                label={NEEDS_LABEL[n] || n}
                size="small"
                sx={({ palette, functions: { rgba } }) => ({
                  height: 22,
                  fontSize: 11.5,
                  fontWeight: 700,
                  backgroundColor: rgba(palette.warning.main, 0.16),
                  color: palette.warning.focus,
                })}
              />
            ))}

          {row.state === "skipped" && (
            <Chip
              label={`أُجّل بواسطة ${row.user_name || "أحدهم"}`}
              size="small"
              sx={{ height: 22, fontSize: 11.5, opacity: 0.75 }}
            />
          )}
        </SuiBox>
      </SuiBox>

      {asking === "measure" && (
        <SuiBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            label="T.CBM"
            type="number"
            size="small"
            value={cbm}
            onChange={(e) => setCbm(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 120 }}
          />
          <TextField
            label="T.KG"
            type="number"
            size="small"
            value={kg}
            onChange={(e) => setKg(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 120 }}
          />
          <TextField
            label="D.Date"
            type="date"
            size="small"
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
        </SuiBox>
      )}

      {asking === "reschedule" && (
        <TextField
          label="يوم التحميل الجديد"
          type="date"
          size="small"
          value={loadingDay}
          onChange={(e) => setLoadingDay(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 190 }}
        />
      )}

      <SuiBox sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
        {isMeasure && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={busy}
            startIcon={<Icon>check</Icon>}
            onClick={() =>
              asking === "measure"
                ? onAnswer(row, "fill", { total_cbm: cbm, total_kg: kg, delivery_date: delivery })
                : setAsking("measure")
            }
            sx={{ minWidth: 96 }}
          >
            {asking === "measure" ? "حفظ" : "إدخال"}
          </Button>
        )}

        {isNote && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={busy}
            startIcon={<Icon>check</Icon>}
            onClick={() =>
              asking === "reschedule"
                ? onAnswer(row, "reschedule", { expected_loading_date: loadingDay })
                : onAnswer(row, "done")
            }
            sx={{ minWidth: 96 }}
          >
            {asking === "reschedule" ? "حفظ" : "تم"}
          </Button>
        )}

        {isNote && (
          <Button
            variant="outlined"
            color="dark"
            size="small"
            disabled={busy}
            onClick={() => setAsking(asking === "reschedule" ? "" : "reschedule")}
            sx={{ minWidth: 96 }}
          >
            {asking === "reschedule" ? "رجوع" : "تأجيل التحميل"}
          </Button>
        )}

        <Button
          variant="outlined"
          color="dark"
          size="small"
          disabled={busy}
          onClick={() => (asking ? setAsking("") : onAnswer(row, "skip"))}
          sx={{ minWidth: 96 }}
        >
          {asking ? "رجوع" : "تخطي"}
        </Button>

        {/* only the chase offers it: the answer to "no deposit has arrived"
            is sometimes that the order is not happening */}
        {row.step === "check" && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={busy || Boolean(asking)}
            onClick={() => onAnswer(row, "cancel")}
            sx={{ minWidth: 96 }}
          >
            إلغاء الطلبية
          </Button>
        )}
      </SuiBox>
    </SuiBox>
  );
}

OrderTask.defaultProps = { busy: false };
OrderTask.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  row: PropTypes.object.isRequired,
  onAnswer: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

Task.defaultProps = { busy: false };
Task.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  row: PropTypes.object.isRequired,
  onDone: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

function Notifications() {
  const [due, setDue] = useState([]);
  const [done, setDone] = useState([]);
  const [ready, setReady] = useState(true);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("due");
  const [query, setQuery] = useState("");
  // how many notices were cleared off the list and are being held back
  const [held, setHeld] = useState(0);

  /**
   * Both families, on one screen. The order workflow had a server and a
   * settings screen and no reader at all - a chase notice set to one minute
   * fired into nothing, because this page only ever asked about containers.
   */
  const load = async () => {
    try {
      const [containers, orders] = await Promise.all(
        [`${API}/container-tasks`, `${API}/order-tasks`].map((url) =>
          fetch(url, { cache: "no-store" })
            .then((r) => r.json())
            // one family being unavailable must not empty the other
            .catch(() => ({}))
        )
      );

      setReady(containers.ready !== false);
      setNote(containers.note || "");
      setDue([
        ...(Array.isArray(containers.due) ? containers.due : []),
        ...(Array.isArray(orders.due) ? orders.due : []),
      ]);
      setDone(Array.isArray(containers.done) ? containers.done : []);
      setHeld((Number(containers.cleared) || 0) + (Number(orders.cleared) || 0));
      setError("");
    } catch (e) {
      setError("Could not load the notifications");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  /**
   * Everything waiting, taken off the list at once - and the way back.
   *
   * A notice is worked out from a date rather than stored, so a year of
   * imported orders arrives as a wall of things that are all still "true",
   * and the one that matters today is somewhere in it. Clearing is not تم:
   * nothing is recorded as done and no date is written anywhere - the server
   * remembers which notices were cleared, and for which due date, so a
   * container or order entered afterwards still notifies, and correcting a
   * date brings that notice back. "Bring back" undoes the whole thing.
   */
  const clearing = async (restore) => {
    setBusy(true);
    try {
      await Promise.all(
        [`${API}/container-tasks/clear`, `${API}/order-tasks/clear`].map((url) =>
          axios.post(
            url,
            { restore, ...currentUser() },
            { headers: { Accept: "application/json", "Content-Type": "application/json" } }
          )
        )
      );
      await load();
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Could not clear the notifications");
    }
    setBusy(false);
  };

  const clearAll = () => {
    const sure = window.confirm(
      `Clear all ${due.length} notifications?\n\n` +
        "Nothing is marked as done. New orders and containers will still notify, " +
        "and you can bring these back from this page."
    );

    if (sure) clearing(false);
  };

  const answer = async (row, action, date) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/container-tasks`,
        {
          container_id: row.container_id,
          step: row.step,
          action,
          date: date || null,
          ...currentUser(),
        },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );
      await load();
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save");
    }
    setBusy(false);
  };

  /** The order family answers a different endpoint, with the order's id. */
  const answerOrder = async (row, action, extra) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/order-tasks`,
        { order_id: row.order_id, step: row.step, action, ...(extra || {}), ...currentUser() },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );
      await load();
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save");
    }
    setBusy(false);
  };

  /**
   * Which shape a waiting row is drawn as. An order row carries an order_id;
   * a container row does not, and the delivery-note reminder carries both -
   * which is why the order is asked about first.
   */
  const waitingRow = (row) =>
    row.order_id ? (
      <OrderTask
        key={`order-${row.order_id}-${row.step}`}
        row={row}
        busy={busy}
        onAnswer={answerOrder}
      />
    ) : (
      <Task
        key={`container-${row.container_id}-${row.step}`}
        row={row}
        busy={busy}
        onDone={(r, date) => answer(r, "done", date)}
        onSkip={(r) => answer(r, "skip")}
      />
    );

  const rows = tab === "due" ? due : done;
  const filtered = filterRows(rows, query);
  const pager = usePagination(filtered, 12);

  const overdue = due.filter((d) => d.overdue_days > 0).length;

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Notifications"
              value={query}
              onChange={setQuery}
              count={filtered.length}
              total={rows.length}
              actions={
                tab === "due" && (due.length > 0 || held > 0) ? (
                  <>
                    {held > 0 && (
                      <Button size="small" disabled={busy} onClick={() => clearing(true)}>
                        {`Bring back ${held} cleared`}
                      </Button>
                    )}
                    {due.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={busy}
                        onClick={clearAll}
                      >
                        Clear all
                      </Button>
                    )}
                  </>
                ) : null
              }
            >
              {[
                { key: "due", label: `Waiting (${due.length})` },
                { key: "done", label: `Done (${done.length})` },
              ].map((t) => (
                <Chip
                  key={t.key}
                  size="small"
                  label={t.label}
                  onClick={() => setTab(t.key)}
                  variant={tab === t.key ? "filled" : "outlined"}
                  sx={({ palette, functions: { rgba } }) => ({
                    fontWeight: tab === t.key ? 700 : 500,
                    cursor: "pointer",
                    backgroundColor:
                      tab === t.key ? rgba(palette.primary.main, 0.18) : "transparent",
                    borderColor: rgba(palette.dark.main, 0.15),
                  })}
                />
              ))}

              {overdue > 0 && tab === "due" && (
                <Chip
                  label={`${overdue} overdue`}
                  size="small"
                  sx={({ palette, functions: { rgba } }) => ({
                    fontWeight: 700,
                    backgroundColor: rgba(palette.error.main, 0.16),
                    color: palette.error.main,
                  })}
                />
              )}
            </FilterBar>

            {!ready && (
              <SuiBox px={3} pb={2}>
                <Alert severity="info">
                  {note || "The workflow tables are not set up on the server yet."}
                </Alert>
              </SuiBox>
            )}

            {error && (
              <SuiBox px={3} pb={2}>
                <Alert severity="error">{error}</Alert>
              </SuiBox>
            )}

            {loading ? (
              <CargoLoader label="Loading notifications" />
            ) : (
              <SuiBox px={3} pb={3}>
                {/* "all caught up" only when there is something to be caught
                    up with - on a server without the tables the list is empty
                    because nothing can be read, which is the opposite of fine
                    and already has its own notice above */}
                {pager.pageRows.length === 0 && ready && (
                  <SuiBox sx={{ py: 6, textAlign: "center" }}>
                    <SuiTypography variant="button" color="text">
                      {tab === "due" ? "Nothing waiting - all caught up" : "Nothing finished yet"}
                    </SuiTypography>
                  </SuiBox>
                )}

                <SuiBox sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {pager.pageRows.map((row) =>
                    tab === "due" ? (
                      waitingRow(row)
                    ) : (
                      /* the record of who did what - the reason the answers
                         are kept at all once the step is gone from the list */
                      <SuiBox
                        key={`${row.container_id}-${row.step}`}
                        sx={({ palette, borders, functions: { rgba } }) => ({
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                          px: 2.5,
                          py: 1.5,
                          borderRadius: borders.borderRadius.lg,
                          border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
                          borderInlineStartWidth: 4,
                          borderInlineStartColor: palette.success.main,
                        })}
                      >
                        <Icon sx={({ palette }) => ({ color: palette.success.main })}>
                          check_circle
                        </Icon>

                        <SuiBox sx={{ flex: "1 1 300px", minWidth: 0 }}>
                          <SuiTypography sx={{ fontSize: 15, fontWeight: 700 }}>
                            {row.label}
                          </SuiTypography>
                          {/* a finished step still names a container worth opening */}
                          {row.container_id ? (
                            <RecordLink row={row}>{row.container_name}</RecordLink>
                          ) : (
                            <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }}>
                              {row.container_name}
                            </SuiTypography>
                          )}
                        </SuiBox>

                        <SuiBox sx={{ textAlign: "end" }}>
                          <SuiTypography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                            {row.user_name || "unknown"}
                          </SuiTypography>
                          <SuiTypography sx={{ fontSize: 12, opacity: 0.6 }}>
                            {String(row.done_at || "")
                              .slice(0, 16)
                              .replace("T", " ")}
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                    )
                  )}
                </SuiBox>

                <Pagination {...pager} />
              </SuiBox>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
